import React, { useState } from 'react';
import {
  AlertCircle,
  Hash,
  BookOpen,
  Edit3,
  FileText,
  Package,
  Percent,
  Plus,
  Search,
  ShoppingCart,
  Tag
} from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { useToast } from '../../../../../hooks/useToast';
import { FinanceProduct, PRESET_PRODUCTS, STORAGE_KEYS, formatEuro, nextCode } from './financeMasters';
import { useStoredList } from './useStoredList';
import '../../shared-modules.css';
import './FinanceMasters.css';

type ProductForm = Omit<FinanceProduct, 'id'>;

const EMPTY_FORM: ProductForm = {
  type: 'Bien',
  name: '',
  sku: '',
  description: '',
  price: 0,
  incomeAccount: '700000',
  vatRate: 4,
  isPurchasedFromSupplier: false,
  expenseAccount: '600000'
};

const INCOME_ACCOUNTS: Record<string, string> = {
  '700000': 'Ventas de mercaderías (Libros)',
  '705000': 'Prestación de servicios (BaaS / Edición)'
};

const EXPENSE_ACCOUNTS: Record<string, string> = {
  '600000': 'Compras de mercaderías (Papel / Tiradas)',
  '607000': 'Trabajos realizados por otras empresas (Imprenta)',
  '624000': 'Transportes y envíos',
  '629000': 'Otros servicios'
};

const VAT_RATES: { value: number; label: string }[] = [
  { value: 4, label: '4% - Superreducido (Libros y revistas)' },
  { value: 10, label: '10% - Reducido (Contenidos mixtos)' },
  { value: 21, label: '21% - General (Servicios y envíos)' },
  { value: 0, label: '0% - Exento' }
];

interface FinanceProductsViewProps {
  privacyMode: boolean;
}

export const FinanceProductsView = ({ privacyMode }: FinanceProductsViewProps) => {
  const { notify } = useToast();

  const [products, setProducts] = useStoredList<FinanceProduct>(STORAGE_KEYS.products, PRESET_PRODUCTS);

  const [selectedProduct, setSelectedProduct] = useState<FinanceProduct | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProductForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  const setField = <K extends keyof ProductForm>(field: K, value: ProductForm[K]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleCreateNewClick = () => {
    setSelectedProduct(null);
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (product: FinanceProduct) => {
    const { id, ...rest } = product;
    setSelectedProduct(product);
    setFormData({ ...EMPTY_FORM, ...rest, expenseAccount: product.expenseAccount || '600000' });
    setIsEditing(true);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectProduct = (product: FinanceProduct) => {
    setIsFormOpen(false);
    setSelectedProduct(product);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedProduct(null);
  };

  // Clic fuera cierra la ficha, el formulario no
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedProduct) && !isFormOpen);

  const handleSaveProduct = () => {
    if (!formData.name.trim()) {
      setFormError('El nombre del artículo o servicio es obligatorio.');
      return;
    }
    if (formData.price < 0) {
      setFormError('El precio de venta no puede ser negativo.');
      return;
    }
    const data: ProductForm = {
      ...formData,
      name: formData.name.trim(),
      sku: formData.sku.trim() || `SKU-${Date.now().toString().slice(-6)}`,
      description: formData.description.trim(),
      expenseAccount: formData.isPurchasedFromSupplier ? formData.expenseAccount : ''
    };

    if (isEditing && selectedProduct) {
      const updated: FinanceProduct = { ...selectedProduct, ...data };
      setProducts(prev => prev.map(p => (p.id === updated.id ? updated : p)));
      setSelectedProduct(updated);
      notify('Éxito', `Artículo/Servicio "${updated.name}" modificado correctamente.`, 'info');
    } else {
      const created: FinanceProduct = { ...data, id: nextCode('P', products.map(p => p.id)) };
      setProducts(prev => [created, ...prev]);
      setSelectedProduct(created);
      notify('Éxito', `Artículo/Servicio "${created.name}" añadido al catálogo.`, 'info');
    }
    setIsFormOpen(false);
  };

  const query = searchQuery.toLowerCase();
  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(query) ||
    p.sku.toLowerCase().includes(query) ||
    (p.description || '').toLowerCase().includes(query)
  );

  const columns: ColumnDef<FinanceProduct>[] = [
    {
      id: 'sku',
      header: 'CÓDIGO / SKU',
      render: (p) => <span className="fin-master__code">{p.sku}</span>
    },
    {
      id: 'name',
      header: 'NOMBRE',
      render: (p) => (
        <>
          <div className="fin-master__name">{p.name}</div>
          {p.description && <div className="fin-master__desc">{p.description}</div>}
        </>
      )
    },
    {
      id: 'type',
      header: 'TIPO',
      render: (p) => <span className={`fin-master__type ${p.type === 'Bien' ? 'good' : 'service'}`}>{p.type}</span>
    },
    {
      id: 'price',
      header: 'PRECIO',
      align: 'right',
      render: (p) => <span className="fin-master__amount">{formatEuro(p.price, privacyMode)}</span>
    },
    {
      id: 'incomeAccount',
      header: 'CUENTA INGRESO',
      render: (p) => <span className="fin-master__chip">{p.incomeAccount}</span>
    },
    {
      id: 'vatRate',
      header: 'TIPO IVA',
      align: 'center',
      render: (p) => <span className="fin-master__amount">{p.vatRate}%</span>
    },
    {
      id: 'purchase',
      header: 'COMPRA PROVEEDOR',
      align: 'center',
      render: (p) => p.isPurchasedFromSupplier
        ? <span className="module-badge active">Sí ({p.expenseAccount})</span>
        : <span className="fin-master__muted">No</span>
    }
  ];

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR NOMBRE, SKU O DESCRIPCIÓN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
          <Plus size={14} /> Nuevo_Artículo_Servicio
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedProduct || isFormOpen)}>
        <DataTable
          data={filteredProducts}
          columns={columns}
          title={<><Package size={14} className="module-panel-title-icon" /> Maestro_De_Artículos_Y_Servicios</>}
          subtitle={`TOTAL: ${filteredProducts.length}`}
          getRowId={(p) => p.id}
          onRowClick={handleSelectProduct}
          selectedRowId={selectedProduct?.id}
          emptyText="No se encontraron artículos ni servicios."
          actionsHeader="ACCIONES"
          actionsColumn={(p) => (
            <RowActionsMenu
              actions={[
                viewAction(() => handleSelectProduct(p)),
                editAction(() => handleEditClick(p))
              ]}
            />
          )}
        />

        {isFormOpen ? (
          <SidebarForm
            expandable
            title={isEditing ? 'Modificar_Artículo_Servicio' : 'Alta_De_Artículo_Servicio'}
            onClose={closePanel}
            submitLabel="Guardar_Artículo"
            onSubmit={handleSaveProduct}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection title="1. Datos_Del_Artículo" icon={FileText}>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Tipo *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setField('type', e.target.value as FinanceProduct['type'])}
                    className="sidebar-form-input"
                  >
                    <option value="Bien">Bien (Producto físico / Libro)</option>
                    <option value="Servicio">Servicio (Suscripción / Edición / Envío)</option>
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Código / SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setField('sku', e.target.value)}
                    placeholder="Ej. 978-84-1355-300-0"
                    className="sidebar-form-input"
                  />
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Nombre del Artículo / Servicio *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Ej. Manual de Derecho Mercantil"
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Precio de Venta (€) *</label>
                <input
                  type="number"
                  required
                  min={0}
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setField('price', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Descripción</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="Detalles sobre el formato, edición, páginas o alcance del servicio..."
                  className="sidebar-form-input"
                />
              </div>
            </DetailSection>

            <DetailSection title="2. Contabilidad_E_Impuestos" icon={BookOpen}>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Cuenta de Ingreso (Venta) *</label>
                <select
                  value={formData.incomeAccount}
                  onChange={(e) => setField('incomeAccount', e.target.value)}
                  className="sidebar-form-input"
                >
                  {Object.keys(INCOME_ACCOUNTS).map(code => (
                    <option key={code} value={code}>{code} - {INCOME_ACCOUNTS[code]}</option>
                  ))}
                </select>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Tipo de IVA Aplicable *</label>
                <select
                  value={formData.vatRate}
                  onChange={(e) => setField('vatRate', parseInt(e.target.value, 10))}
                  className="sidebar-form-input"
                >
                  {VAT_RATES.map(v => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
              </div>
              <label className="fin-master__check">
                <input
                  type="checkbox"
                  checked={formData.isPurchasedFromSupplier}
                  onChange={(e) => setField('isPurchasedFromSupplier', e.target.checked)}
                />
                Se compra a un proveedor
              </label>
              {formData.isPurchasedFromSupplier && (
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Cuenta de Gasto Asociada (Compras) *</label>
                  <select
                    value={formData.expenseAccount}
                    onChange={(e) => setField('expenseAccount', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {Object.keys(EXPENSE_ACCOUNTS).map(code => (
                      <option key={code} value={code}>{code} - {EXPENSE_ACCOUNTS[code]}</option>
                    ))}
                  </select>
                </div>
              )}
            </DetailSection>
          </SidebarForm>
        ) : selectedProduct ? (
          <DetailPanel
            expandable
            title="Ficha_De_Artículo_Servicio"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedProduct)} />
            }
          >
            <DetailIdentity
              icon={Package}
              reference={selectedProduct.id}
              name={selectedProduct.name}
              subtitle={
                <span className={`fin-master__type ${selectedProduct.type === 'Bien' ? 'good' : 'service'}`}>
                  {selectedProduct.type}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={Hash} label="CÓDIGO_SKU">{selectedProduct.sku}</DetailField>
              <DetailField icon={Tag} label="PRECIO_DE_VENTA">{formatEuro(selectedProduct.price, privacyMode)}</DetailField>
              <DetailField icon={BookOpen} label="CUENTA_DE_INGRESO">
                {`${selectedProduct.incomeAccount} - ${INCOME_ACCOUNTS[selectedProduct.incomeAccount] || ''}`}
              </DetailField>
              <DetailField icon={Percent} label="TIPO_DE_IVA">{`${selectedProduct.vatRate}%`}</DetailField>
              <DetailField icon={ShoppingCart} label="COMPRA_A_PROVEEDOR">
                {selectedProduct.isPurchasedFromSupplier
                  ? `Sí - ${selectedProduct.expenseAccount} ${EXPENSE_ACCOUNTS[selectedProduct.expenseAccount] || ''}`
                  : 'No'}
              </DetailField>
            </DetailFieldList>

            {selectedProduct.description && (
              <DetailNotes title="Descripción">{selectedProduct.description}</DetailNotes>
            )}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceProductsView;
